import { CampaignEmailingEnum, CampaignFormValues } from '@/client/campaigns';
import { TranslationFunctionType } from '@/common/types';
import { FormikCheckbox, FormikInput } from '@/components/form';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithCustomTimepicker } from '@/ui/calendar/AppCalendarWithCustomTimepicker';
import { FlexContainer } from '@/ui/styled-ui';
import { checkIfNowOrAfter, isBeforeJobStartCheck } from '@/utils/helpers';
import { Field } from 'formik';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type PeriodStepProps = {
  initialValues: CampaignFormValues;
  values: CampaignFormValues;
  valuesSetter: any;
  campaignId?: string;
};

export const PeriodStep: React.FC<PeriodStepProps> = ({
  initialValues,
  values,
  valuesSetter,
  campaignId,
}) => {
  const { t } = useTranslation();
  const [canEditName, setCanEditName] = useState(false);
  const [showDynamicTargetError, setShowDynamicTargetError] = useState(false);

  useEffect(() => {
    setShowDynamicTargetError(
      isBeforeJobStartCheck(values.launchDate) && !!values.useDynamicTarget,
    );
  }, [values.launchDate, values.useDynamicTarget]);

  useEffect(() => {
    if (values.isAutoPhishing) {
      if (
        (values.launchDate || values.sendByDate) &&
        (values.launchDate !== initialValues.launchDate ||
          values.sendByDate !== initialValues.sendByDate)
      ) {
        const targetDate =
          values?.periodType === CampaignEmailingEnum.DRIP
            ? values.sendByDate
            : values.launchDate;

        if (!targetDate) return;

        const completeAfter = new Date(targetDate);
        const currentDate = new Date(completeAfter);

        const isStartDateWeekend =
          targetDate.getDay() === 6 || targetDate.getDay() === 0;

        let businessDaysAdded = 0;
        while (businessDaysAdded < 3) {
          currentDate.setDate(currentDate.getDate() + 1);
          const dayOfWeek = currentDate.getDay();

          // Skip weekends (Saturday = 6, Sunday = 0)
          if (dayOfWeek !== 6 && dayOfWeek !== 0) {
            businessDaysAdded++;
          }
        }

        if (isStartDateWeekend) {
          currentDate.setHours(23, 59, 59, 999);
        }

        valuesSetter('completeAfter', currentDate);
      }
    }
  }, [
    values.isAutoPhishing,
    values.launchDate,
    values.sendByDate,
    values.periodType,
  ]);

  const emailingTypes = (t: TranslationFunctionType) => [
    {
      label: t('campaign.drip'),
      value: CampaignEmailingEnum.DRIP,
    },
    {
      label: t('campaign.blast'),
      value: CampaignEmailingEnum.BLAST,
    },
  ];

  return (
    <>
      <h2>{t('campaign.period')}</h2>

      <div className="field-checkbox mb-3" data-id="isAutoPhishing">
        <Field
          inputId="isAutoPhishing"
          name="isAutoPhishing"
          label={t('campaign.auto.phishing')}
          component={FormikCheckbox}
          onChange={(e: CheckboxChangeEvent) =>
            valuesSetter('isAutoPhishing', e.checked)
          }
        />
      </div>

      <div className="field w-6">
        <label htmlFor="emailing-type">
          {t('campaign.emailType')}
          <span className="red"> *</span>
        </label>
        <Dropdown
          id="emailing-type"
          className="w-full"
          placeholder={t('generic.select')}
          value={values.periodType}
          onChange={(e) => valuesSetter('periodType', e.value)}
          options={emailingTypes(t)}
          data-testid="campaign-form-period-step-emailing-type"
        />
      </div>

      <div className="field w-6">
        <label htmlFor="launch-date">
          {t('generic.launchDate')}
          <span className="red"> *</span>
        </label>
        <AppCalendarWithCustomTimepicker
          id="launch-date"
          name="launchDate"
          className="w-full"
          onFocus={() => {
            if (!values.launchDate || !checkIfNowOrAfter(values.launchDate))
              valuesSetter('launchDate', new Date());
          }}
          minDate={new Date()}
          value={values.launchDate}
          showTime
          onChange={(value: Date | null) => valuesSetter('launchDate', value)}
          disabled={!values.periodType}
          readOnlyInput
          data-testid="campaign-form-period-step-launch-date"
          withHolidays
        />
        {showDynamicTargetError && (
          <small className="p-error">
            {t('campaign.dynamicTarget.launchTime.warning')}
          </small>
        )}
      </div>

      {values.periodType === CampaignEmailingEnum.DRIP ? (
        <div className="field w-6">
          <label htmlFor="send-by">
            {t('campaign.sendDate')}
            <span className="red"> *</span>
          </label>
          <AppCalendarWithCustomTimepicker
            id="send-by-date"
            name="sendByDate"
            value={values.sendByDate}
            showTime
            onFocus={() => {
              if (!values.sendByDate || !checkIfNowOrAfter(values.sendByDate))
                valuesSetter('sendByDate', values.launchDate || new Date());
            }}
            minDate={new Date()}
            className="w-full"
            onChange={(value: Date | null) => valuesSetter('sendByDate', value)}
            readOnlyInput
            data-testid="campaign-form-period-step-send-by-date"
          />
        </div>
      ) : null}

      <div className="field w-6">
        <label htmlFor="complete-after-date">
          {t('generic.completeAfter')}
        </label>
        <AppCalendarWithCustomTimepicker
          id="complete-after-date"
          name="completeAfter"
          className="w-full"
          minDate={new Date()}
          value={values.completeAfter}
          showTime
          viewDate={values.sendByDate || values.launchDate || new Date()}
          onChange={(value: Date | null) =>
            valuesSetter('completeAfter', value)
          }
          disabled={!values.periodType}
          disabledDays={values?.isAutoPhishing ? [6, 0] : undefined}
          readOnlyInput
          data-testid="campaign-form-period-step-complete-after-date"
          withHolidays
        />
      </div>

      <FlexContainer justify="flex-start" align="flex-end" className="mb-4">
        <div className="field w-6 m-0 relative">
          <Field
            id="name"
            name="name"
            label={t('generic.name')}
            value={values.name}
            className="w-full"
            onChange={(event: any) => valuesSetter('name', event.target.value)}
            component={FormikInput}
            disabled={!canEditName}
            data-testid="campaign-form-period-step-name"
          />
        </div>
        {!canEditName ? (
          <AppButton
            icon="pi pi-pencil"
            type="text"
            onClick={() => setCanEditName(true)}
            className="ml-2"
            data-testid="campaign-form-period-step-enable-edit-name"
          />
        ) : null}
      </FlexContainer>

      {values.meta?.cloneOf && (
        <>
          <div className="field w-6">
            <label htmlFor="id">{t('generic.id')}</label>
            <InputText
              id="id"
              name="id"
              value={campaignId}
              className="w-full"
              disabled
              data-testid="campaign-form-period-step-cloneOf"
            />
          </div>

          <h3 className="mb-4">{t('generic.cloneOf')}</h3>
          <div className="field w-6">
            <label htmlFor="cloneOfName">{t('campaign.template.name')}</label>
            <InputText
              id="cloneOfName"
              name="cloneOfName"
              value={values.meta?.cloneOfName}
              className="w-full"
              disabled
              data-testid="campaign-form-period-step-cloneOfName"
            />
          </div>
          <div className="field w-6">
            <label htmlFor="benchmark">
              {t('campaign.template.phishingType')}
            </label>
            <InputText
              id="benchmark"
              name="benchmark"
              value={values.benchmark?.name}
              className="w-full"
              disabled
              data-testid="campaign-form-period-step-phishingType"
            />
          </div>
          <div className="field w-6">
            <label htmlFor="phishingSign">
              {t('campaign.template.phishingSign')}
            </label>
            <InputText
              id="phishingSign"
              name="phishingSign"
              value={t(`campaign.template.${values.meta.phishingSign}`)}
              className="w-full"
              disabled
              data-testid="campaign-form-period-step-phishingSign"
            />
          </div>
        </>
      )}
    </>
  );
};
